import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  type FormEventHandler,
} from 'react';
import { X } from '@phosphor-icons/react';
import { createPortal } from 'react-dom';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import {
  hasOpenFloatingUiInsideContainer,
  isFloatingUiTargetOwnedByContainer,
} from '@/lib/dom/floatingUiContainerOwnership';
import {
  getOverlayViewportPaddingStyle,
  useOverlayViewportInsets,
} from '@/components/overlays/OverlayViewportInsets';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

let nextModalStackId = 0;
const openModalStack: number[] = [];

function getNextModalStackId(): number {
  nextModalStackId += 1;
  return nextModalStackId;
}

function pushOpenModal(id: number): void {
  openModalStack.push(id);
}

function removeOpenModal(id: number): void {
  const index = openModalStack.lastIndexOf(id);
  if (index !== -1) {
    openModalStack.splice(index, 1);
  }
}

function isTopOpenModal(id: number): boolean {
  return openModalStack[openModalStack.length - 1] === id;
}

type ModalSize = 'default' | 'medium' | 'wide' | 'full';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  titleClassName?: string;
  labelledBy?: string;
  describedBy?: string;
  closeLabel?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  closeOnBackdropClick?: boolean;
  disableClose?: boolean;
  showCloseButton?: boolean;
  showHeader?: boolean;
  onSubmit?: FormEventHandler<HTMLFormElement>;
  size?: ModalSize;
  bodyClassName?: string;
  className?: string;
  backdropClassName?: string;
  headerActions?: React.ReactNode;
}

// Backdrop styles
const backdropClasses = [
  'fixed inset-0',
  'bg-overlay/60',
  'flex items-center justify-center',
  'z-overlay',
  'p-6 md:p-6 max-md:p-4',
  'overflow-y-auto',
  'animate-fade-in',
  'transition-all duration-300 ease-in-out',
].join(' ');

// Base modal window styles
const windowBaseClasses = [
  'bg-raised text-glyph-primary',
  'rounded-xl',
  'outline-none',
  'relative z-dialog',
  'flex min-h-0 flex-col',
  'animate-slide-up',
  'shadow-default border border-outline-default',
].join(' ');

// Size variants
const windowSizeClasses: Record<ModalSize, string> = {
  default: 'w-full max-w-screen-sm max-h-modal',
  medium: 'w-full max-w-[760px] max-h-modal',
  wide: 'w-full max-w-modal-wide max-h-modal',
  full: 'w-full max-w-modal-full max-h-modal-full',
};

// Header styles
const headerClasses = [
  'flex items-center justify-between',
  'px-6 py-3',
  'bg-transparent',
  'border-b border-outline-default/40',
  'rounded-t-xl',
  'gap-4',
].join(' ');

// Title styles (size/color via titleClassName or default below)
const titleBaseClasses = 'font-semibold font-display m-0 tracking-tight';
const defaultTitleClasses = 'text-base text-glyph-primary';

// Close button styles
const closeClasses = [
  'appearance-none border-none bg-transparent',
  'text-glyph-primary',
  'p-0 h-11 w-11 shrink-0',
  'rounded-full cursor-pointer',
  'inline-flex items-center justify-center',
  'hover:bg-accent-primary/10 hover:text-accent-primary transition-colors duration-fast',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-glyph-primary focus-visible:outline-offset-2',
  'disabled:text-glyph-primary/50 disabled:cursor-default',
].join(' ');

// Body styles
const bodyClasses = 'min-h-0 p-6 overflow-y-auto overflow-x-hidden';

// The body owns the gap above the footer, so vertical padding is not doubled.
const footerClasses = 'px-6 pb-6 flex items-center justify-end gap-4 flex-wrap';

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  titleClassName,
  labelledBy,
  describedBy,
  closeLabel = 'Close dialog',
  children,
  footer,
  closeOnBackdropClick = true,
  disableClose = false,
  showCloseButton = true,
  showHeader = true,
  onSubmit,
  size = 'default',
  bodyClassName,
  className,
  backdropClassName,
  headerActions,
}) => {
  const dialogRef = useRef<HTMLDivElement | HTMLFormElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const modalStackIdRef = useRef<number | null>(null);
  const generatedTitleId = useId();
  const onCloseRef = useRef(onClose);
  const overlayViewportInsets = useOverlayViewportInsets();
  const overlayViewportPaddingStyle = getOverlayViewportPaddingStyle(overlayViewportInsets, '1rem');
  const overlayViewportDialogStyle = overlayViewportInsets
    ? ({ maxHeight: '100%' } satisfies React.CSSProperties)
    : undefined;

  if (modalStackIdRef.current === null) {
    modalStackIdRef.current = getNextModalStackId();
  }
  const modalStackId = modalStackIdRef.current;

  useBodyScrollLock(isOpen);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    pushOpenModal(modalStackId);

    return () => {
      removeOpenModal(modalStackId);
    };
  }, [isOpen, modalStackId]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const resolvedLabelId = labelledBy ?? (title ? generatedTitleId : undefined);

  const handleClose = useCallback(() => {
    if (disableClose) return;
    onCloseRef.current();
  }, [disableClose]);

  const handleOutsideMouseDown = useCallback(
    (event: MouseEvent) => {
      if (!closeOnBackdropClick || disableClose) return;
      if (!isTopOpenModal(modalStackId)) return;
      const dialogEl = dialogRef.current;
      if (!dialogEl) return;

      const target = event.target as Node;
      if (dialogEl.contains(target)) return;
      if (isFloatingUiTargetOwnedByContainer(target, dialogEl)) return;

      handleClose();
    },
    [closeOnBackdropClick, disableClose, handleClose, modalStackId]
  );

  const trapFocus = useCallback((event: KeyboardEvent) => {
    const dialogEl = dialogRef.current;
    if (!dialogEl) return;

    const focusable = dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusable.length === 0) {
      event.preventDefault();
      const targetFocus = dialogEl as HTMLElement;
      if (document.activeElement !== targetFocus) {
        targetFocus.focus();
      }
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;

    if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    }
  }, []);

  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return undefined;
    }

    previouslyFocusedElement.current = document.activeElement as HTMLElement | null;
    const dialogEl = dialogRef.current;

    const focusFirst = () => {
      if (!dialogEl) return;
      const focusable = dialogEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const preferred = Array.from(focusable).find(
        (node) => node.dataset.modalInitialFocus === 'true'
      );
      const first = preferred ?? focusable[0];
      const nodeToFocus = first ?? (dialogEl as HTMLElement);
      nodeToFocus.focus({ preventScroll: true });
    };

    focusFirst();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isTopOpenModal(modalStackId)) {
        return;
      }

      if (event.key === 'Escape') {
        if (!disableClose && dialogEl && !hasOpenFloatingUiInsideContainer(dialogEl)) {
          event.preventDefault();
          handleClose();
        }
      } else if (event.key === 'Tab') {
        trapFocus(event);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleOutsideMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideMouseDown);
      previouslyFocusedElement.current?.focus({ preventScroll: true });
    };
  }, [handleClose, isOpen, disableClose, trapFocus, handleOutsideMouseDown, modalStackId]);

  const labelProps = useMemo(() => {
    if (labelledBy) {
      return { 'aria-labelledby': labelledBy };
    }
    if (showHeader && title) {
      return { 'aria-labelledby': resolvedLabelId };
    }
    if (title) {
      return { 'aria-label': title };
    }
    return {};
  }, [labelledBy, showHeader, title, resolvedLabelId]);

  const portalTarget = useMemo(() => {
    if (typeof document === 'undefined') {
      return null;
    }
    return document.body;
  }, []);

  if (!isOpen || portalTarget == null) {
    return null;
  }

  const baseContent = (
    <>
      {showHeader && (
        <header className={headerClasses}>
          {title && (
            <h2
              id={resolvedLabelId}
              className={[titleBaseClasses, titleClassName ?? defaultTitleClasses]
                .filter(Boolean)
                .join(' ')}
            >
              {title}
            </h2>
          )}
          <div
            className={`ml-auto flex shrink-0 items-center gap-2 ${showCloseButton ? '-mr-3' : ''}`}
          >
            {headerActions}
            {showCloseButton && (
              <button
                type="button"
                className={closeClasses}
                onClick={handleClose}
                aria-label={closeLabel}
                disabled={disableClose}
              >
                <X size={18} weight="bold" aria-hidden="true" />
              </button>
            )}
          </div>
        </header>
      )}
      <div className={[bodyClasses, bodyClassName].filter(Boolean).join(' ')}>{children}</div>
      {footer && <footer className={footerClasses}>{footer}</footer>}
    </>
  );

  const containerClassName = [windowBaseClasses, windowSizeClasses[size], className]
    .filter(Boolean)
    .join(' ');

  const dialog = onSubmit ? (
    <form
      ref={dialogRef as React.Ref<HTMLFormElement>}
      className={containerClassName}
      role="dialog"
      aria-modal="true"
      {...labelProps}
      aria-describedby={describedBy}
      tabIndex={-1}
      onSubmit={onSubmit as FormEventHandler<HTMLFormElement>}
      style={{
        backdropFilter: 'var(--card-backdrop)',
        ...overlayViewportDialogStyle,
      }}
    >
      {baseContent}
    </form>
  ) : (
    <div
      ref={dialogRef as React.Ref<HTMLDivElement>}
      className={containerClassName}
      role="dialog"
      aria-modal="true"
      {...labelProps}
      aria-describedby={describedBy}
      tabIndex={-1}
      style={{
        backdropFilter: 'var(--card-backdrop)',
        ...overlayViewportDialogStyle,
      }}
    >
      {baseContent}
    </div>
  );

  return createPortal(
    <div
      className={[backdropClasses, backdropClassName].filter(Boolean).join(' ')}
      role="presentation"
      style={{
        backdropFilter: 'var(--card-backdrop)',
        ...overlayViewportPaddingStyle,
      }}
    >
      {dialog}
    </div>,
    portalTarget
  );
};
